import { useState } from 'react'

import {
  detailText,
  dnpReason,
  fullCells,
  phoneCells,
  rosterGroups,
  shortName,
  teamNote,
  totalsCells,
} from './gameModel.js'
import { TeamMark } from './Marks.jsx'
import { PROVISIONAL, fptsText, playerFpts } from './fantasyModel.js'

// The box score: one section per team. P6-328b (GN-1b).
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 FOUR COLUMNS ON A PHONE, A TAP FOR THE REST, EVERY COLUMN FROM 720px
// ═══════════════════════════════════════════════════════════════════════════
//
// A box score has a dozen columns and a phone has room for four, and the design's answer is
// neither a sideways scroll (nothing on this site scrolls sideways at 320px, measured by
// `viewport.test.js` in a real browser) nor a shrunk font: MIN, PTS, REB and AST are inline and
// a TAP on a row opens that player's full line underneath it. From 720px every column is inline
// and the rows are plain. The two are different elements (`layout.js` says why), so `wide`
// arrives as a prop.
//
// BELOW 360px THE NAME IS THE SURNAME ALONE (P6-343, the design's `phoneBox && fw < 360 ? sur(p.name) : short(p.name)`):
// `Brunson`, not `J. Brunson`, so a long surname keeps its room in the four-column row. `narrow` arrives as a prop from
// `GameView.jsx`, which asks `layout.js::useBelowWidth` once; the default is the full form (see that hook on why).
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 `–` IS "NO LINE", AND IT IS NEVER `0`
// ═══════════════════════════════════════════════════════════════════════════
//
// A player who has not played has `null` for every figure (`gameModel.js` on why). A bench
// player who has not entered the game is a row of dashes; he is not a row of zeros, and he is not
// tappable, because there is no line to open. A player the feed lists as NOT PLAYING (an injury,
// a two-way player on assignment) is not a row at all: he is named, with his reason, under the
// team's total.
//
// ⚠️ NO "ON THE FLOOR" MARKER IN A ROW. The bot publishes `oncourt` for every player and the design draws a
// dot beside the five a team has out there. P6-330b (GN-3b) draws the five as a section of their own
// under the hero (`Floor.jsx`), for a game being played, and the brief did not ask for the dot as well:
// it would be the same five players shown another way, and a dot on a FINAL game's last five would say
// they are still out there. The data is in the blob; the marker is one element if it is ever wanted.

const PHONE_HEAD = ['Player', 'Min', 'Pts', 'Reb', 'Ast']
const FULL_HEAD = ['Player', 'Min', 'FG', '3PT', 'FT', 'Reb', 'Ast', 'Stl', 'Blk', 'TO', 'PF', '+/-', 'Pts']

// P6-333a (GN-6a): the viewer's league adds ONE column, Fpts, at the end (the design's `fpCol`), for EVERY player who has a
// line, scored by the viewer's own league; and it lights the viewer's own players' rows in ember. `fantasy` is
// `{ stats, mine }` (`fantasyModel.js`) or `null`, and `null` draws exactly the box score this file always drew. The
// figure is provisional, and the box says so in the column head's accessible name, not in a word per row.

const dash = (value) => (value === null || value === undefined ? '–' : value)

/** `strongIndex` is the one figure a row sets in the heavier weight: points, which is the second
 * cell on a phone and the last of the twelve from 720px. */
function Cells({ values, strongIndex = -1 }) {
  return values.map((value, index) => (
    <span
      className="d2d-hoops__pcell"
      data-strong={index === strongIndex ? 'true' : 'false'}
      data-empty={value === null ? 'true' : 'false'}
      key={index}
    >
      {dash(value)}
    </span>
  ))
}

/** What a screen reader hears for a phone row, whose figures are bare numbers without column heads. */
function phoneLabel(player, cells, fpts) {
  const [min, pts, reb, ast] = cells
  return (
    `${player.name ?? 'Player'}: ` +
    `${dash(min)} minutes, ${dash(pts)} points, ${dash(reb)} rebounds, ${dash(ast)} assists` +
    (fpts === undefined ? '' : `, ${fpts === null ? 'no' : fpts} fantasy points, ${PROVISIONAL}`)
  )
}

function PlayerRow({ player, wide, narrow, open, onToggle, fantasy }) {
  const hasLine = player.played === true
  const fpts = fantasy ? playerFpts(player, fantasy.stats) : undefined
  const mine = Boolean(fantasy) && fantasy.mine.has(player.person_id)
  const base = wide ? fullCells(player) : phoneCells(player)
  const cells = fantasy ? [...base, fpts === null ? null : fptsText(fpts)] : base
  // On a phone the row is a button that opens the full line. A row with no line has nothing to
  // open, and a wide row has every column already.
  const interactive = !wide && hasLine
  const strongIndex = wide ? base.length - 1 : 1
  const nameCell = (
    <span className="d2d-hoops__pname">
      <span className="d2d-hoops__pshort" data-played={hasLine ? 'true' : 'false'}>
        {shortName(player.name, narrow)}
      </span>
      {wide && player.position ? <span className="d2d-hoops__ppos">{player.position}</span> : null}
    </span>
  )
  if (!interactive) {
    return (
      <div
        className="d2d-hoops__prow d2d-hoops__prow--static"
        data-testid="hoops-box-row"
        data-mine={mine ? 'true' : undefined}
      >
        {nameCell}
        <Cells values={cells} strongIndex={strongIndex} />
      </div>
    )
  }
  return (
    <>
      <button
        type="button"
        className="d2d-hoops__prow"
        data-testid="hoops-box-row"
        data-mine={mine ? 'true' : undefined}
        aria-expanded={open ? 'true' : 'false'}
        aria-label={phoneLabel(player, base, fantasy ? (fpts === null ? null : fptsText(fpts)) : undefined)}
        onClick={onToggle}
      >
        {nameCell}
        <Cells values={cells} strongIndex={strongIndex} />
      </button>
      {open ? (
        <div className="d2d-hoops__pdetail" data-testid="hoops-box-detail">
          {detailText(player)}
        </div>
      ) : null}
    </>
  )
}

function TeamBox({ which, side, team, players, totals, wide, narrow, expanded, onToggle, fantasy }) {
  const groups = rosterGroups(players)
  const baseHead = wide ? FULL_HEAD : PHONE_HEAD
  const head = fantasy ? [...baseHead, 'Fpts'] : baseHead
  const note = teamNote(totals, side)
  const rows = (list) =>
    list.map((player) => (
      <PlayerRow
        key={player.person_id}
        player={player}
        wide={wide}
        narrow={narrow}
        open={expanded.has(player.person_id)}
        onToggle={() => onToggle(player.person_id)}
        fantasy={fantasy}
      />
    ))
  return (
    <section
      className={`d2d-hoops__box d2d-hoops__box--${wide ? 'wide' : 'phone'}${fantasy ? ' d2d-hoops__box--fpts' : ''}`}
      data-testid="hoops-box"
      data-side={which}
      aria-label={`${side.city ?? ''} ${side.name ?? side.tricode} box score`.trim()}
    >
      <div className="d2d-hoops__boxhead">
        <TeamMark team={team} tricode={side.tricode} which={which} size="small" />
        <span className="d2d-hoops__boxname">{`${side.city ?? ''} ${side.name ?? side.tricode}`.trim()}</span>
        {note === '' ? null : <span className="d2d-hoops__boxnote d2d-micro">{note}</span>}
      </div>

      <div className="d2d-hoops__cols" aria-hidden="true">
        {head.map((label, index) => (
          <span className="d2d-hoops__col" data-first={index === 0 ? 'true' : 'false'} key={label}>
            {label}
          </span>
        ))}
      </div>

      {groups.starters.length > 0 ? <div className="d2d-hoops__group d2d-micro">Starters</div> : null}
      {rows(groups.starters)}
      {groups.bench.length > 0 ? <div className="d2d-hoops__group d2d-micro">Bench</div> : null}
      {rows(groups.bench)}

      <div className="d2d-hoops__tot" data-testid="hoops-box-total">
        <span className="d2d-hoops__totlabel d2d-micro">Team</span>
        <Cells values={totalsCells(totals ?? {}, !wide, Boolean(fantasy))} />
      </div>

      {groups.notPlaying.length > 0 ? (
        <>
          <div className="d2d-hoops__group d2d-micro">Not playing</div>
          {groups.notPlaying.map((player) => (
            <div className="d2d-hoops__dnp" key={player.person_id} data-testid="hoops-box-dnp">
              <span className="d2d-hoops__dnpname">{shortName(player.name, narrow)}</span>
              <span className="d2d-hoops__dnpwhy" title={player.not_playing_reason}>
                {dnpReason(player.not_playing_reason)}
              </span>
            </div>
          ))}
        </>
      ) : null}

      {wide ? null : (
        <p className="d2d-hoops__boxhint">
          Tap a player for shooting, steals, blocks, turnovers, fouls and +/-.
        </p>
      )}
    </section>
  )
}

export default function BoxScore({ game, teamOf, wide, narrow = false, fantasy = null }) {
  // Which rows are open, by player. A phone-only state: a wide box has every column showing.
  const [expanded, setExpanded] = useState(() => new Set())
  const toggle = (personId) =>
    setExpanded((previous) => {
      const next = new Set(previous)
      if (next.has(personId)) next.delete(personId)
      else next.add(personId)
      return next
    })
  return (
    <div className="d2d-hoops__boxes" data-testid="hoops-boxes">
      {[
        ['away', game.away, game.players.away, game.totals.away],
        ['home', game.home, game.players.home, game.totals.home],
      ].map(([which, side, players, totals]) => (
        <TeamBox
          key={which}
          which={which}
          side={side}
          team={teamOf[which]}
          players={players}
          totals={totals}
          wide={wide}
          narrow={narrow}
          expanded={expanded}
          onToggle={toggle}
          fantasy={fantasy}
        />
      ))}
    </div>
  )
}
